import {Box, Stack, Typography} from '@mui/material';
import React, {Fragment, useCallback, useEffect, useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';

import {TypeLocalNameEnum} from 'domain/usecases';
import {
	CTAButtonFixed,
	ContactInfo,
	DataCard,
	MainFooter,
	MainHeader,
	PlanTypeChip,
	StyledGrid,
} from 'presentation/components';
import {ModalCompleteYourProfile} from 'presentation/components/ModalCompleteYourProfile';
import {HomeEnum, ProfileEnum, SelectionFlowEnum} from 'presentation/constants';
import {useBeneficiaryCard, usePersonalData} from 'presentation/hooks';
import Icon from 'presentation/icons';
import {useAccountInformation, useMediaQueries} from 'presentation/pages/private/hooks';
import {translations} from 'presentation/translations-new';
import {getBookingFlowPath, getPageHeaderTitle} from 'presentation/utils';

import {useIsComingFromInitialFlowStore} from 'presentation/pages/others/selection-flow/store/useIsComingFromInitialFlowStore';
import {InitialLocationCard} from '../../components';

import {useBookingFlowStepsStore} from 'presentation/modules/booking/shared/store/useBookingFlowStepsStore';
import {useBookingStore} from 'presentation/modules/booking/shared/store/useBookingStore';
import {ConfirmContactInfoDialog} from '../../components/ConfirmContactInfoDialog';
import {BookingFlowType, FlowEnum, PATH_TITLE, ufNameMap} from '../../constants';

import {StyledBox, StyledSubHeader} from './styles';
import {InitialStepProps} from './types';
import {getBookingTypeAndOrigin} from 'presentation/modules/booking/shared/utils/getBookingTypeAndOrigin';

const {changeBeneficiary, confirmSelection} = translations['pt-br'].commons;
const {confirmDataInfoStep} = translations['pt-br'].privateFlow.bookingFlow;

const {booking} = translations['pt-br'].others.sidebar;

export function InitialStep({flowType}: InitialStepProps) {
	const setBookingData = useBookingStore(state => state.setBookingData);
	const resetBookingData = useBookingStore(state => state.resetBookingData);
	const resetBookingFlowStep = useBookingFlowStepsStore(state => state.resetBookingStep);
	const {isDesktop, isLaptop, isMobile} = useMediaQueries();
	const {beneficiaryCardPlanData} = useBeneficiaryCard();
	const {currentLocation: storedLocation} = useAccountInformation();
	const setIsComingFromInitialFlow = useIsComingFromInitialFlowStore(
		state => state.setIsComingFromInitialFlow,
	);

	const navigate = useNavigate();
	const {state: routeState, pathname} = useLocation();
	const {currentEmail, currentPhone, userLocation: profileLocation} = usePersonalData();

	const currentLocation = {
		cdUf: storedLocation?.state?.cdUf || profileLocation?.nmUf,
		nmUf: storedLocation?.state?.nmUf || ufNameMap[profileLocation?.nmUf],
		nmCity: storedLocation?.city || profileLocation?.nmCidade,
		typeLocalName: storedLocation?.typeLocalName || TypeLocalNameEnum.CIDADE,
		reference: storedLocation?.reference,
	};

	const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

	const pageTitle = getPageHeaderTitle({internalPaths: PATH_TITLE, mainPath: booking});
	const bookingFlowPath = getBookingFlowPath(flowType);
	const isTelehealthFlow = flowType === FlowEnum.TELEHEALTH;

	const emptyContacts = !currentPhone || !currentEmail;
	const emptyLocation =
		!currentLocation?.nmCity || !currentLocation?.cdUf || !currentLocation?.nmUf;

	const shouldNotProceed = isTelehealthFlow ? emptyContacts : emptyLocation || emptyContacts;

	const handleBack = useCallback(() => {
		if (routeState) {
			navigate(routeState);
			return;
		}
		if (routeState && routeState.includes('/servicos/guia-medico')) {
			navigate(routeState);
			return;
		}

		navigate(HomeEnum.HOME_PAGE);
	}, [routeState]);

	const handleClose = useCallback(() => {
		navigate(HomeEnum.HOME_PAGE);
	}, []);

	const handleConfirmContactInfo = useCallback(() => {
		setIsConfirmModalOpen(true);
	}, []);

	const handleCloseConfirmModal = useCallback(() => {
		navigate(ProfileEnum.PROFILE);
		setIsConfirmModalOpen(false);
	}, []);

	function goToBookingFlow() {
		const {bookingType, bookingOrigin} = getBookingTypeAndOrigin(flowType as BookingFlowType);

		setBookingData(prev => ({
			...prev,
			email: currentEmail,
			phone: currentPhone,
			ufCode: isTelehealthFlow ? 'CE' : currentLocation.cdUf,
			ufName: isTelehealthFlow ? 'CEARA' : currentLocation.nmUf,
			cityName: isTelehealthFlow ? 'FORTALEZA' : currentLocation.nmCity,
			typeLocalName: currentLocation.typeLocalName,
			reference: currentLocation.reference,
			bookingType,
			bookingOrigin,
		}));

		navigate(bookingFlowPath, {
			replace: true,
		});
	}

	const handleChangeBeneficiary = () => {
		setIsComingFromInitialFlow(true);
		navigate(SelectionFlowEnum.BENEFICIARY_SELECTION, {
			replace: true,
			state: {
				pathFrom: pathname,
			},
		});
	};

	useEffect(() => {
		resetBookingData();
		resetBookingFlowStep();
	}, []);

	return (
		<Fragment>
			<MainHeader
				title={pageTitle}
				handleBack={handleBack}
				handleClose={handleClose}
				variant="subRoute">
				<StyledSubHeader>
					<Typography variant="h6" color="white">
						{confirmDataInfoStep}
					</Typography>
				</StyledSubHeader>
			</MainHeader>

			<StyledGrid.Root p={isMobile ? 2 : 4} flex={1} spacing={0}>
				<StyledGrid.Item sm={12} md={12} lg={8}>
					<Stack gap={2}>
						{isLaptop ? (
							<DataCard
								title={beneficiaryCardPlanData?.userName}
								subtitle={beneficiaryCardPlanData?.planName}
								subtitleColor="text.secondary"
								titleColor="text.secondary"
								buttonText={changeBeneficiary}
								handleAction={handleChangeBeneficiary}
								footerComponent={<PlanTypeChip />}
							/>
						) : null}
						{isTelehealthFlow ? null : <InitialLocationCard flowType={flowType} />}
						<ContactInfo
							user={{
								cellphone: currentPhone,
								email: currentEmail,
							}}
						/>
						<CTAButtonFixed.Root>
							<CTAButtonFixed.Action
								fullWidth={isMobile}
								onClick={handleConfirmContactInfo}
								disabled={shouldNotProceed}
								sx={{alignSelf: 'flex-end'}}
								size="large"
								endIcon={<Icon name="arrow-right-filled" />}>
								{confirmSelection}
							</CTAButtonFixed.Action>
						</CTAButtonFixed.Root>
					</Stack>
				</StyledGrid.Item>
				{isDesktop ? (
					<StyledGrid.Item lg={4}>
						<Box pl={2}>
							<DataCard
								title={beneficiaryCardPlanData.userName}
								subtitle={beneficiaryCardPlanData.planName}
								subtitleColor="text.secondary"
								titleColor="text.secondary"
								buttonText={changeBeneficiary}
								handleAction={handleChangeBeneficiary}
								footerComponent={<PlanTypeChip />}
							/>
						</Box>
					</StyledGrid.Item>
				) : null}
			</StyledGrid.Root>

			<StyledBox>
				<MainFooter />
			</StyledBox>

			{isConfirmModalOpen ? (
				<ConfirmContactInfoDialog
					onPrimaryAction={goToBookingFlow}
					onSecondaryAction={handleCloseConfirmModal}
					open={isConfirmModalOpen}
					setOpen={setIsConfirmModalOpen}
				/>
			) : null}
			<ModalCompleteYourProfile />
		</Fragment>
	);
}
